import React, { useEffect, useRef } from 'react';

// v6 — the scroll journey, refined:
//  · CENTER: 3D particle formation morphing BRC → sphere → torus → galaxy,
//    exploding with scroll velocity and reassembling at rest
//  · TWO satellites only (torus top-right, sphere bottom-left) with HUD arc
//    rings — they WAKE on hover: spin up, brighten, swell toward the cursor
//  · a silk light-ribbon chases the pointer (eased chain), tinted by the
//    current station, shedding tiny sparkles as it moves
// Reduced-motion settles a static frame; hidden tabs pause; half-res render.

const N = 3000;
const STATIONS = 4;
const TINTS = [
    [0, 242, 254],
    [79, 172, 254],
    [139, 92, 246],
    [255, 0, 128],
];
const TRAIL_LEN = 26;

export default function BpBackground() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let w = 0, h = 0, raf = 0, running = true, t = 0;
        let stars = [], sats = [], sparkles = [], streams = [];
        const RES = 0.6;
        const dpr = Math.min(window.devicePixelRatio || 1, 1.5) * RES;
        const mouse = { x: 0.5, y: 0.5, px: -9999, py: -9999, on: false };
        const trail = Array.from({ length: TRAIL_LEN }, () => ({ x: -9999, y: -9999 }));

        const rand = (a, b) => a + Math.random() * (b - a);

        /* ---------- center formation targets ---------- */
        const shapes = [];
        const scatter = new Float32Array(N * 3);
        const cur = new Float32Array(N * 3);
        const jit = new Float32Array(N);

        const buildShapes = () => {
            const off = document.createElement('canvas');
            off.width = 360; off.height = 140;
            const o = off.getContext('2d');
            o.fillStyle = '#fff';
            o.font = '900 118px Inter, Arial, sans-serif';
            o.textAlign = 'center'; o.textBaseline = 'middle';
            o.fillText('BRC', 180, 76);
            const img = o.getImageData(0, 0, 360, 140).data;
            const pts = [];
            for (let y = 0; y < 140; y += 2) {
                for (let x = 0; x < 360; x += 2) {
                    if (img[(y * 360 + x) * 4 + 3] > 128) pts.push([x, y]);
                }
            }
            const brc = new Float32Array(N * 3);
            for (let i = 0; i < N; i++) {
                const [px, py] = pts[Math.floor(Math.random() * pts.length)];
                brc[i * 3] = (px - 180) / 128;
                brc[i * 3 + 1] = (py - 70) / 128;
                brc[i * 3 + 2] = rand(-0.05, 0.05);
            }
            shapes[0] = brc;

            const sph = new Float32Array(N * 3);
            const GA = Math.PI * (3 - Math.sqrt(5));
            for (let i = 0; i < N; i++) {
                const yy = 1 - (i / (N - 1)) * 2;
                const rr = Math.sqrt(1 - yy * yy);
                const th = GA * i;
                sph[i * 3] = Math.cos(th) * rr * 0.85;
                sph[i * 3 + 1] = yy * 0.85;
                sph[i * 3 + 2] = Math.sin(th) * rr * 0.85;
            }
            shapes[1] = sph;

            const tor = new Float32Array(N * 3);
            for (let i = 0; i < N; i++) {
                const u = rand(0, Math.PI * 2), v = rand(0, Math.PI * 2);
                const R = 0.78, r = 0.26;
                tor[i * 3] = (R + r * Math.cos(v)) * Math.cos(u);
                tor[i * 3 + 1] = r * Math.sin(v);
                tor[i * 3 + 2] = (R + r * Math.cos(v)) * Math.sin(u);
            }
            shapes[2] = tor;

            const gal = new Float32Array(N * 3);
            for (let i = 0; i < N; i++) {
                const arm = i % 3;
                const tt = Math.pow(Math.random(), 0.72) * 2.3 * Math.PI;
                const rr = 0.1 + tt * 0.135;
                const th = tt + (arm * Math.PI * 2) / 3 + rand(-0.18, 0.18);
                gal[i * 3] = Math.cos(th) * rr;
                gal[i * 3 + 1] = rand(-0.05, 0.05) * (1.2 - rr);
                gal[i * 3 + 2] = Math.sin(th) * rr;
            }
            shapes[3] = gal;

            for (let i = 0; i < N; i++) {
                const th = rand(0, Math.PI * 2), ph = Math.acos(rand(-1, 1));
                const m = rand(0.6, 1.6);
                scatter[i * 3] = Math.sin(ph) * Math.cos(th) * m;
                scatter[i * 3 + 1] = Math.cos(ph) * m;
                scatter[i * 3 + 2] = Math.sin(ph) * Math.sin(th) * m;
                jit[i] = rand(0, Math.PI * 2);
                cur[i * 3] = scatter[i * 3] * 2.4;
                cur[i * 3 + 1] = scatter[i * 3 + 1] * 2.4;
                cur[i * 3 + 2] = scatter[i * 3 + 2] * 2.4;
            }
        };

        /* ---------- the two satellites ---------- */
        const miniTorus = (n) => {
            const a = new Float32Array(n * 3);
            for (let i = 0; i < n; i++) {
                const u = rand(0, Math.PI * 2), v = rand(0, Math.PI * 2);
                a[i * 3] = (0.75 + 0.25 * Math.cos(v)) * Math.cos(u);
                a[i * 3 + 1] = 0.25 * Math.sin(v);
                a[i * 3 + 2] = (0.75 + 0.25 * Math.cos(v)) * Math.sin(u);
            }
            return a;
        };
        const miniSphere = (n) => {
            const a = new Float32Array(n * 3);
            const GA = Math.PI * (3 - Math.sqrt(5));
            for (let i = 0; i < n; i++) {
                const yy = 1 - (i / (n - 1)) * 2;
                const rr = Math.sqrt(1 - yy * yy);
                const th = GA * i;
                a[i * 3] = Math.cos(th) * rr;
                a[i * 3 + 1] = yy;
                a[i * 3 + 2] = Math.sin(th) * rr;
            }
            return a;
        };

        const build = () => {
            w = window.innerWidth; h = window.innerHeight;
            const slim = w < 820;
            canvas.width = Math.max(2, w * dpr);
            canvas.height = Math.max(2, h * dpr);
            canvas.style.width = w + 'px';
            canvas.style.height = h + 'px';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            stars = Array.from({ length: Math.round((w * h) / 16000) }, () => ({
                x: rand(0, w), y: rand(0, h), r: rand(0.4, 1.3),
                tw: rand(0, Math.PI * 2), sp: rand(0.01, 0.04),
            }));

            sats = [
                {
                    pts: miniTorus(slim ? 170 : 280),
                    fx: 0.875, fy: 0.17, s: 0.085,
                    ry: rand(0, 6), rx: 0.5, vy: 0.004,
                    depth: 0.8, hue: [0, 242, 254], excite: 0,
                },
                {
                    pts: miniSphere(slim ? 140 : 240),
                    fx: 0.105, fy: 0.78, s: 0.072,
                    ry: rand(0, 6), rx: -0.3, vy: -0.003,
                    depth: 0.6, hue: [139, 92, 246], excite: 0,
                },
            ];
            sparkles = [];

            // corner streams — particle rivers spanning edge to edge
            const mkStream = (p0, p1, p2, hue, count, dir) => {
                const parts = [];
                for (let i = 0; i < count; i++) {
                    parts.push({
                        s0: Math.random(),
                        speed: rand(0.0006, 0.0016) * dir,
                        off: (Math.random() + Math.random() + Math.random() - 1.5) / 1.5, // gaussian-ish [-1,1]
                        size: rand(0.8, 2),
                        ph: rand(0, Math.PI * 2),
                        tw: rand(0.015, 0.04),
                    });
                }
                return { p0, p1, p2, hue, parts, excite: 0 };
            };
            const sc = slim ? 120 : 230;
            streams = [
                mkStream([-0.04, 0.34], [0.16, 0.02], [0.62, -0.05], [0, 242, 254], sc, 1),
                mkStream([0.38, 1.05], [0.84, 0.98], [1.04, 0.60], [255, 0, 128], sc, -1),
            ];
        };

        /* ---------- journey state ---------- */
        let prog = 0, dispersion = 0, rotY = 0;
        let lastScrollY = window.scrollY;

        const drawSatellite = (sat, cr, cg, cbl) => {
            const scaleBase = Math.min(w, h) * sat.s;
            const px = sat.fx * w + (mouse.x - 0.5) * sat.depth * 34;
            const py = sat.fy * h + (mouse.y - 0.5) * sat.depth * 24 + Math.sin(t * 0.008 + sat.depth * 9) * 8;

            // hover: wake up when the cursor comes near
            const hoverR = scaleBase * 3.2;
            const dd = Math.hypot(mouse.px - px, mouse.py - py);
            const want = mouse.on && dd < hoverR ? 1 : 0;
            sat.excite += (want - sat.excite) * 0.07;
            const ex = sat.excite;

            sat.ry += sat.vy * (1 + ex * 3.2) + dispersion * 0.008;
            const scale = scaleBase * (1 + ex * 0.22);
            // lean gently toward the cursor when awake
            const lx = px + (mouse.px - px) * 0.045 * ex;
            const ly = py + (mouse.py - py) * 0.045 * ex;

            const sy = Math.sin(sat.ry), cy = Math.cos(sat.ry);
            const sx = Math.sin(sat.rx + ex * 0.35), cx = Math.cos(sat.rx + ex * 0.35);
            const r = (sat.hue[0] * 0.55 + cr * 0.45) | 0;
            const g = (sat.hue[1] * 0.55 + cg * 0.45) | 0;
            const b = (sat.hue[2] * 0.55 + cbl * 0.45) | 0;
            const pts = sat.pts;
            for (let i = 0; i < pts.length; i += 3) {
                const x = pts[i], y = pts[i + 1], z = pts[i + 2];
                const x1 = x * cy - z * sy, z1 = x * sy + z * cy;
                const y1 = y * cx - z1 * sx, z2 = y * sx + z1 * cx;
                const s = 2.6 / (2.6 + z2 + 1.2);
                const a = Math.max(0.08, s * s * (0.5 + ex * 0.45));
                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
                const sz = 1.4 + ex * 0.8;
                ctx.fillRect(lx + x1 * scale * s, ly + y1 * scale * s, sz, sz);
            }
            // halo blooms as it wakes
            if (ex > 0.03) {
                const halo = ctx.createRadialGradient(lx, ly, 0, lx, ly, scale * 2.1);
                halo.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.10 * ex})`);
                halo.addColorStop(1, 'rgba(0,0,0,0)');
                ctx.fillStyle = halo;
                ctx.fillRect(lx - scale * 2.1, ly - scale * 2.1, scale * 4.2, scale * 4.2);
            }
            // HUD arc rings — spin up with excitement
            const spin = t * (0.006 + ex * 0.02);
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.22 + ex * 0.3})`;
            ctx.lineWidth = 1;
            ctx.setLineDash([3, 7]);
            ctx.beginPath();
            ctx.arc(lx, ly, scale * 1.55, spin, spin + Math.PI * (1.4 + ex * 0.5));
            ctx.stroke();
            ctx.setLineDash([1, 12]);
            ctx.beginPath();
            ctx.arc(lx, ly, scale * 1.85, -spin * 0.7, -spin * 0.7 + Math.PI * 1.1);
            ctx.stroke();
            ctx.setLineDash([]);
        };

        const drawStream = (st, cr, cg, cbl) => {
            const [x0, y0] = st.p0, [x1, y1] = st.p1, [x2, y2] = st.p2;
            const P0x = x0 * w, P0y = y0 * h, P1x = x1 * w, P1y = y1 * h, P2x = x2 * w, P2y = y2 * h;
            const spreadBase = Math.min(w, h) * 0.035;
            // stream-level excitement eases in while the cursor rides the river
            let near = 0;
            if (mouse.on) {
                // sample a few points along the curve for cursor proximity
                for (let k = 0; k <= 6; k++) {
                    const u = k / 6, iu = 1 - u;
                    const bx = iu * iu * P0x + 2 * iu * u * P1x + u * u * P2x;
                    const by = iu * iu * P0y + 2 * iu * u * P1y + u * u * P2y;
                    const d = Math.hypot(mouse.px - bx, mouse.py - by);
                    near = Math.max(near, Math.max(0, 1 - d / (Math.min(w, h) * 0.24)));
                }
            }
            st.excite += (near - st.excite) * 0.08;
            const ex = st.excite;

            const r = (st.hue[0] * 0.6 + cr * 0.4) | 0;
            const g = (st.hue[1] * 0.6 + cg * 0.4) | 0;
            const b = (st.hue[2] * 0.6 + cbl * 0.4) | 0;

            // faint spine glow so the river reads as a path
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.05 + ex * 0.07})`;
            ctx.lineWidth = 22 + ex * 16;
            ctx.beginPath();
            ctx.moveTo(P0x, P0y);
            ctx.quadraticCurveTo(P1x, P1y, P2x, P2y);
            ctx.stroke();
            ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.10 + ex * 0.10})`;
            ctx.lineWidth = 5;
            ctx.beginPath();
            ctx.moveTo(P0x, P0y);
            ctx.quadraticCurveTo(P1x, P1y, P2x, P2y);
            ctx.stroke();

            for (const p of st.parts) {
                const u = ((p.s0 + t * p.speed) % 1 + 1) % 1;
                const iu = 1 - u;
                const bx = iu * iu * P0x + 2 * iu * u * P1x + u * u * P2x;
                const by = iu * iu * P0y + 2 * iu * u * P1y + u * u * P2y;
                // tangent -> unit perpendicular
                const txv = 2 * iu * (P1x - P0x) + 2 * u * (P2x - P1x);
                const tyv = 2 * iu * (P1y - P0y) + 2 * u * (P2y - P1y);
                const tl = Math.hypot(txv, tyv) || 1;
                const nx = -tyv / tl, ny = txv / tl;

                // breathing spread + travelling spread-wave along the river
                let spread = spreadBase * (1 + 0.5 * Math.sin(u * 9 - t * 0.03 + p.ph) + 0.25 * Math.sin(t * 0.012));
                let bright = 0;
                if (mouse.on) {
                    const d = Math.hypot(mouse.px - bx, mouse.py - by);
                    const R = Math.min(w, h) * 0.17;
                    if (d < R) {
                        const f = 1 - d / R;
                        spread += spreadBase * f * f * 5.2;      // the river PARTS around the cursor
                        bright = f * 0.6;
                    }
                }

                const px = bx + nx * p.off * spread;
                const py = by + ny * p.off * spread;
                // fade toward both ends so it dissolves off-screen gracefully
                const endFade = Math.min(1, u * 6, (1 - u) * 6);
                const twk = 0.5 + 0.5 * Math.sin(p.ph + t * p.tw);
                const a = endFade * (0.16 + 0.34 * twk + bright + ex * 0.10);
                if (a < 0.02) continue;
                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${Math.min(1, a)})`;
                const sz = p.size * (1 + bright);
                ctx.fillRect(px, py, sz, sz);
                if (bright > 0.3) {
                    ctx.fillStyle = `rgba(255, 255, 255, ${bright * 0.5})`;
                    ctx.fillRect(px, py, 1, 1);
                }
            }
        };

        const drawTrail = (cr, cg, cbl) => {
            // eased chain: head chases the pointer, links chase their parent
            trail[0].x += (mouse.px - trail[0].x) * 0.42;
            trail[0].y += (mouse.py - trail[0].y) * 0.42;
            for (let i = 1; i < TRAIL_LEN; i++) {
                trail[i].x += (trail[i - 1].x - trail[i].x) * 0.42;
                trail[i].y += (trail[i - 1].y - trail[i].y) * 0.42;
            }
            if (!mouse.on) return;
            const speed = Math.hypot(trail[0].x - trail[2].x, trail[0].y - trail[2].y);
            if (speed < 1.2) return;                    // collapses quietly at rest
            const vivid = Math.min(1, speed / 60);

            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            for (let i = 0; i < TRAIL_LEN - 1; i++) {
                const f = 1 - i / TRAIL_LEN;            // 1 at head → 0 at tail
                const a = f * f * (0.28 + vivid * 0.4);
                if (a < 0.01) continue;
                // wide soft pass
                ctx.strokeStyle = `rgba(${cr | 0}, ${cg | 0}, ${cbl | 0}, ${a * 0.35})`;
                ctx.lineWidth = 10 * f + 2;
                ctx.beginPath();
                ctx.moveTo(trail[i].x, trail[i].y);
                ctx.lineTo(trail[i + 1].x, trail[i + 1].y);
                ctx.stroke();
                // bright core pass
                ctx.strokeStyle = `rgba(255, 255, 255, ${a * 0.5})`;
                ctx.lineWidth = 2.2 * f + 0.4;
                ctx.beginPath();
                ctx.moveTo(trail[i].x, trail[i].y);
                ctx.lineTo(trail[i + 1].x, trail[i + 1].y);
                ctx.stroke();
            }
            // shed sparkles from the head while moving fast
            if (vivid > 0.25 && sparkles.length < 50 && (t & 1) === 0) {
                sparkles.push({
                    x: trail[0].x + rand(-3, 3), y: trail[0].y + rand(-3, 3),
                    vx: rand(-0.7, 0.7), vy: rand(-0.9, 0.4),
                    life: 1, r: rand(0.8, 2),
                });
            }
        };

        const drawSparkles = (cr, cg, cbl) => {
            for (let i = sparkles.length - 1; i >= 0; i--) {
                const s = sparkles[i];
                s.x += s.vx; s.y += s.vy; s.vy += 0.015; s.life -= 0.022;
                if (s.life <= 0) { sparkles.splice(i, 1); continue; }
                ctx.fillStyle = `rgba(${(cr + 60) | 0}, ${(cg + 40) | 0}, 255, ${s.life * 0.7})`;
                ctx.fillRect(s.x, s.y, s.r, s.r);
            }
        };

        const drawFrame = () => {
            t += 1;

            const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
            const targetProg = Math.min(1, window.scrollY / maxScroll) * (STATIONS - 1);
            prog += (targetProg - prog) * 0.055;

            const dy = Math.abs(window.scrollY - lastScrollY);
            lastScrollY = window.scrollY;
            dispersion = Math.min(1.35, dispersion + dy * 0.0035);
            dispersion *= 0.955;

            const ia = Math.min(STATIONS - 2, Math.floor(prog));
            const frac0 = prog - ia;
            const frac = frac0 * frac0 * (3 - 2 * frac0);
            const A = shapes[ia], B = shapes[ia + 1];
            const ca = TINTS[ia], cb = TINTS[ia + 1];
            const cr = ca[0] + (cb[0] - ca[0]) * frac;
            const cg = ca[1] + (cb[1] - ca[1]) * frac;
            const cbl = ca[2] + (cb[2] - ca[2]) * frac;

            rotY += 0.0035 + dispersion * 0.012;
            const ry = rotY + (mouse.x - 0.5) * 0.55;
            const rx = -0.12 + (mouse.y - 0.5) * 0.4 + (ia >= 2 ? 0.35 * frac : 0) + (ia === 3 ? 0.35 : 0);
            const sy = Math.sin(ry), cy = Math.cos(ry);
            const sx = Math.sin(rx), cx = Math.cos(rx);

            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = 'rgba(4, 4, 10, 0.32)';
            ctx.fillRect(0, 0, w, h);

            for (const s of stars) {
                const a = 0.14 + 0.18 * Math.sin(s.tw + t * s.sp);
                if (a > 0) { ctx.fillStyle = `rgba(215, 232, 255, ${a})`; ctx.fillRect(s.x, s.y, s.r, s.r); }
            }

            ctx.globalCompositeOperation = 'lighter';
            for (const st of streams) drawStream(st, cr, cg, cbl);
            for (const sat of sats) drawSatellite(sat, cr, cg, cbl);

            const scale = Math.min(w, h) * (0.46 + prog * 0.05 + dispersion * 0.14);
            const cxs = w / 2, cys = h * 0.44;
            const stationDim = (ia === 0 && frac < 0.35) ? (0.5 + dispersion * 0.6 + frac * 1.2) : 1;
            const FOV = 3.2;
            const disp = Math.pow(dispersion, 1.4) * 2.1;
            const mpx = mouse.px, mpy = mouse.py;

            for (let i = 0; i < N; i++) {
                const i3 = i * 3;
                const wob = 0.012 * Math.sin(jit[i] + t * 0.02);
                const tx = A[i3] + (B[i3] - A[i3]) * frac + scatter[i3] * disp + wob;
                const ty = A[i3 + 1] + (B[i3 + 1] - A[i3 + 1]) * frac + scatter[i3 + 1] * disp;
                const tz = A[i3 + 2] + (B[i3 + 2] - A[i3 + 2]) * frac + scatter[i3 + 2] * disp + wob;

                cur[i3] += (tx - cur[i3]) * 0.075;
                cur[i3 + 1] += (ty - cur[i3 + 1]) * 0.075;
                cur[i3 + 2] += (tz - cur[i3 + 2]) * 0.075;

                const x = cur[i3], y = cur[i3 + 1], z = cur[i3 + 2];
                const x1 = x * cy - z * sy, z1 = x * sy + z * cy;
                const y1 = y * cx - z1 * sx, z2 = y * sx + z1 * cx;
                const s = FOV / (FOV + z2 + 1.6);
                let px = cxs + x1 * scale * s;
                let py = cys + y1 * scale * s;

                let boost = 0;
                if (mouse.on) {
                    const ddx = px - mpx, ddy = py - mpy;
                    const d2 = ddx * ddx + ddy * ddy;
                    if (d2 < 15000 && d2 > 1) {
                        const f = (1 - d2 / 15000);
                        const d = Math.sqrt(d2);
                        px += (ddx / d) * f * 26;
                        py += (ddy / d) * f * 26;
                        boost = f * 0.5;              // particles glow as you push them
                    }
                }

                const depth = Math.max(0.15, Math.min(1, s * s * 0.9));
                const size = (1.1 + depth * 1.5) * (1 + dispersion * 0.4 + boost * 0.6);
                const alpha = Math.min(1,
                    depth * (0.5 + 0.3 * Math.sin(jit[i] + t * 0.03)) * (1 - dispersion * 0.25) * Math.min(1, stationDim) + boost);
                ctx.fillStyle = `rgba(${cr | 0}, ${cg | 0}, ${cbl | 0}, ${alpha})`;
                ctx.fillRect(px, py, size, size);
                if ((i & 63) === 0) {
                    ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.7})`;
                    ctx.fillRect(px, py, 1, 1);
                }
            }

            const g = ctx.createRadialGradient(cxs, cys, 0, cxs, cys, scale * 1.5);
            g.addColorStop(0, `rgba(${cr | 0}, ${cg | 0}, ${cbl | 0}, ${0.05 + dispersion * 0.05})`);
            g.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = g;
            ctx.fillRect(cxs - scale * 1.5, cys - scale * 1.5, scale * 3, scale * 3);

            drawTrail(cr, cg, cbl);
            drawSparkles(cr, cg, cbl);

            ctx.globalCompositeOperation = 'source-over';
            const v = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.4, w / 2, h / 2, Math.max(w, h) * 0.8);
            v.addColorStop(0, 'rgba(0,0,0,0)');
            v.addColorStop(1, 'rgba(0,0,0,0.5)');
            ctx.fillStyle = v;
            ctx.fillRect(0, 0, w, h);
        };

        const frame = () => {
            if (!running) return;
            drawFrame();
            raf = requestAnimationFrame(frame);
        };

        const onMove = (e) => {
            mouse.x = e.clientX / w; mouse.y = e.clientY / h;
            if (!mouse.on) {
                for (const p of trail) { p.x = e.clientX; p.y = e.clientY; }
            }
            mouse.px = e.clientX; mouse.py = e.clientY;
            mouse.on = true;
        };
        const onLeave = () => { mouse.on = false; };
        const onClick = () => { dispersion = Math.min(1.35, dispersion + 0.9); };
        const onVis = () => {
            if (document.hidden) { running = false; cancelAnimationFrame(raf); }
            else if (!reduced && !running) { running = true; raf = requestAnimationFrame(frame); }
        };
        let resizeTimer;
        const onResize = () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(build, 150); };

        buildShapes();
        build();
        ctx.fillStyle = '#04040a';
        ctx.fillRect(0, 0, w, h);

        if (reduced) {
            for (let i = 0; i < 90; i++) drawFrame();
        } else {
            raf = requestAnimationFrame(frame);
            window.addEventListener('pointermove', onMove, { passive: true });
            window.addEventListener('pointerleave', onLeave);
            window.addEventListener('pointerdown', onClick, { passive: true });
            document.addEventListener('visibilitychange', onVis);
        }
        window.addEventListener('resize', onResize);

        return () => {
            running = false;
            cancelAnimationFrame(raf);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerleave', onLeave);
            window.removeEventListener('pointerdown', onClick);
            window.removeEventListener('resize', onResize);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, []);

    return <canvas ref={canvasRef} className="bp-bgfx" aria-hidden="true" />;
}
